import type { Ref, ReactNode } from 'react'
import { money } from '../../lib/format'
import { DocHead } from '../../components/ui'

/** 版面依《部落格文章委外驗收單_範本.docx》 */
/** 舊分類的標題全名。category 欄位存的是簡稱，歷史單據的標題要跟當初印的一字不差 */
const GROUP_TITLE: Record<string, string> = {
  '內容品質': '內容品質檢查（A）',
  'SEO規格': 'SEO 規格檢查（B）',
}

/**
 * 驗收單的紙本版面。
 *
 * 抽出來共用，是為了讓「新增驗收單」在存檔前也能預覽同一份版面 ——
 * 兩邊各自畫一次的話，改了列印頁卻忘了改預覽，使用者看到的就不是印出來的東西。
 * 所以這裡只吃資料、不查 API，已存檔的單據與尚未存檔的草稿都能餵進來。
 */
export default function AcceptanceDoc({ a, docRef, banner }: {
  a: any
  /** 下載 PDF 要拿這一塊去渲染 */
  docRef?: Ref<HTMLDivElement>
  /** 放在版面最上方的提示（作廢說明）。放在 .print-doc 裡面，列印與下載 PDF 才都會帶到 */
  banner?: ReactNode
}) {
  /* 依 category 動態分組，不寫死「內容品質／SEO規格」兩組：
     新的驗收單只有一組合併後的檢核，但既有單據仍是舊的兩組，
     兩種都要能正確印出來。順序照 sort 走。 */
  const checkGroups = [...a.checks.reduce((acc: Map<string, any[]>, c: any) => {
    const k = c.category ?? '品質檢核'
    acc.set(k, [...(acc.get(k) ?? []), c])
    return acc
  }, new Map<string, any[]>())]
  const mark = (v: string, target: string) => (v === target ? '■' : '□')

  // 抬頭前綴跟著發包單走，同一件案子兩張單的抬頭要一致。規則與 WorkOrderDoc 相同。
  const lead = a.dispatch_type === '內包' ? '文案內部外包' : '文案委外'

  return (
    <div className="print-doc" ref={docRef}>
      {banner}
      <DocHead lead={lead} title="驗收單" />

      <h2>一、案件基本資料</h2>
      <table>
        <tbody>
          <tr>
            <th style={{ width: '14%' }}>驗收單號</th><td style={{ width: '19%' }}>{a.code}</td>
            <th style={{ width: '14%' }}>承包人</th><td style={{ width: '19%' }}>{a.writer_name}</td>
            <th style={{ width: '14%' }}>發包單號</th><td>{a.work_order_code}</td>
          </tr>
          <tr>
            <th>交付批次</th><td>第 {a.batch_no} 批（共 {a.items.length} 篇）</td>
            <th>約定交付日</th><td>{a.due_on}</td>
            <th>實際交付日</th><td>{a.delivered_on}</td>
          </tr>
          <tr>
            <th>驗收人</th><td>{a.reviewer_name}</td>
            <th>驗收日期</th><td>{a.reviewed_on}</td>
            <th>本批次驗收金額</th><td>NT$ {money(a.amount)}</td>
          </tr>
          <tr>
            <th>稿件交付連結</th>
            <td colSpan={5}>{a.deliver_link}</td>
          </tr>
        </tbody>
      </table>

      <h2>二、交付文章清單</h2>
      <table>
        <thead>
          <tr>
            <th style={{ width: 30 }}>#</th>
            {/* 客戶印簡稱（API 已換成簡稱，沒填簡稱才用正式名稱）。
                客戶欄寬跟上方「驗收單號」那一欄一樣（14%），不會被擠到只剩字寬；
                合約編號縮到剛好放得下（width 1% + nowrap），其餘寬度留給文章標題 */}
            <th style={{ width: '14%' }}>客戶</th>
            <th style={{ width: '1%', whiteSpace: 'nowrap' }}>合約編號</th>
            <th>文章標題</th>
            <th className="num" style={{ width: 48 }}>篇數</th>
            <th className="num" style={{ width: 70 }}>單價</th>
            {/* 夠寬 + nowrap，否則「通過／退修」會被折成兩排 */}
            <th style={{ width: 110, whiteSpace: 'nowrap' }}>單篇結果</th>
          </tr>
        </thead>
        <tbody>
          {a.items.map((it: any, i: number) => (
            <tr key={it.id ?? i}>
              <td>{i + 1}</td>
              <td>{it.client_name}</td>
              <td style={{ whiteSpace: 'nowrap' }}>{it.contract_code}</td>
              <td>{it.title}</td>
              {/* 一列就是一篇，篇數固定 1；合計篇數在下方「本批次通過篇數」 */}
              <td className="num">1</td>
              <td className="num">{money(it.unit_price)}</td>
              <td style={{ whiteSpace: 'nowrap' }}>
                {mark(it.result, '通過')}通過　{mark(it.result, '退修')}退修
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            {/* 金額落在「單價」欄正下方，數字上下對齊才好核對 */}
            <th colSpan={5} className="num">實際金額</th>
            <th className="num">{money(a.amount)}</th>
            <th></th>
          </tr>
        </tfoot>
      </table>

      {checkGroups.map(([name, rows], i) => (
        <div key={name}>
          <h2>{`${'三四五六七'[i] ?? i + 3}、${GROUP_TITLE[name] ?? name}`}</h2>
          <CheckBlock rows={rows} />
        </div>
      ))}

      <h2>{`${'三四五六七'[checkGroups.length] ?? checkGroups.length + 3}、驗收結論`}</h2>
      {/*
        結論選項與備註欄左右並排，兩欄等高。
        原本上下排會佔掉大半頁，紙本常常因此多印一張。
        用 table 而不是 flex：列印時 flex 在部分瀏覽器會被拆頁，table 的儲存格不會。
      */}
      <table className="conclusion-split">
        <tbody>
          <tr>
            {/* 70/30：左欄放結論選項，右欄放備註 */}
            <td style={{ width: '70%' }}>
              <div className="terms">
                {mark(a.conclusion, '驗收通過')}　驗收通過：全部項目符合約定規格，得依合約辦理請款。<br />
                {mark(a.conclusion, '有條件通過')}　有條件通過：缺失項目請配合驗收人要求在指定期限內補正，補正確認後視同通過。<br />
                {mark(a.conclusion, '退修')}　退修：退修項目與說明如「備註」欄，請於 {a.fix_due_days ?? '＿＿'} 日內重新交付。<br />
                {mark(a.conclusion, '驗收不通過')}　驗收不通過：依合約相關條款辦理。
              </div>
            </td>
            <td>
              <div className="remark-label">驗收備註／退件與修改紀錄</div>
              <div style={{ whiteSpace: 'pre-wrap', minHeight: 62 }}>{a.remark}</div>
            </td>
          </tr>
        </tbody>
      </table>

      {/* 標題與簽名格包成一塊：列印跨頁時整組一起搬到下一頁，不會標題留在上一頁、簽名格被切開 */}
      <div className="sign-block">
        <h2>{`${'三四五六七'[checkGroups.length + 1] ?? checkGroups.length + 4}、簽核`}</h2>
        <table className="sign-table">
          <tbody>
            <tr>
              <th style={{ width: '33%' }}>發包人</th>
              <th style={{ width: '33%' }}>驗收人</th>
              <th>直屬主管</th>
            </tr>
          </tbody>
          <tbody className="sign-row">
            <tr><td></td><td></td><td></td></tr>
          </tbody>
        </table>
      </div>
    </div>
  )
}

/**
 * 品質檢核：只完整列出「不符」的項目，符合的濃縮成一行編號，省下大半頁面高度。
 * 九條全名仍以小字附在最下面，紙本上查得到每個編號代表什麼。
 * 「符合」也可能填了備註（表單上是選填），那些要另外列出，不能因為濃縮就被吃掉。
 */
function CheckBlock({ rows }: { rows: any[] }) {
  const ok = rows.filter((c) => c.verdict !== '不符')
  const bad = rows.filter((c) => c.verdict === '不符')
  const okNoted = ok.filter((c) => c.note?.trim())
  return (
    <table className="check-summary">
      <tbody>
        <tr>
          <th style={{ width: 84 }}>符合</th>
          <td>
            {ok.length
              ? `${ok.map((c) => c.code).join('、')}（共 ${ok.length} 項${bad.length ? '' : '，全數符合'}）`
              : '無'}
          </td>
        </tr>
        {bad.map((c) => (
          <tr key={c.code}>
            <th className="bad">不符 {c.code}</th>
            <td>
              {c.label}
              <div className="note">說明：{c.note?.trim() || '（未填）'}</div>
            </td>
          </tr>
        ))}
        {okNoted.map((c) => (
          <tr key={c.code}>
            <th>附註 {c.code}</th>
            <td>
              {c.label}
              <div className="note">{c.note}</div>
            </td>
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr>
          <td colSpan={2} className="legend">
            檢核項目：{rows.map((c) => `${c.code} ${c.label}`).join('　')}
          </td>
        </tr>
      </tfoot>
    </table>
  )
}
